import { React, useState, useEffect, createContext } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import styles from "../assets/css/modules/shipmentReport.module.css";
import axiosInstance from "../helpers/AxiosInstance";
import { Box } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Header,
  Title,
  ShipmentsReportsTable,
  ButtonsContainer,
  LoadingButton,
} from "../components/utilities";
import NewPrintBtn from "../components/utilities/NewPrintBtn";

// Modern styled components
const ModernHeader = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 24px 32px;
  border-radius: 16px;
  margin-bottom: 24px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  
  @media (max-width: 768px) {
    padding: 20px;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const ModernTitle = styled.h1`
  color: white;
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  
  &::before {
    content: '📊';
    font-size: 32px;
  }
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const ModernButtonsContainer = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const ModernButton = styled.button`
  padding: 12px 20px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${(props) => props.primary ? "#1f4f5c" : "white"};
  color: ${(props) => props.primary ? "white" : "#475569"};
  box-shadow: ${(props) => props.primary ? "0 4px 20px rgba(16, 185, 129, 0.3)" : "0 2px 8px rgba(0, 0, 0, 0.1)"};
  border: 2px solid ${(props) => props.primary ? "transparent" : "#e2e8f0"};
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 140px;
  justify-content: center;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${(props) => props.primary ? "0 8px 30px rgba(16, 185, 129, 0.4)" : "0 4px 12px rgba(0, 0, 0, 0.15)"};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: 768px) {
    min-width: 120px;
    padding: 10px 16px;
    font-size: 13px;
  }
`;

const ModernStopButton = styled.button`
  padding: 12px 20px;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(239, 68, 68, 0.3);
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(239, 68, 68, 0.4);
  }
  
  &::before {
    content: '⏹️';
    font-size: 16px;
  }
  
  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 13px;
  }
`;

const ModernTableContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  
  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 12px;
  }
`;
const Button = styled.button`
  position: relative;
  border: 0px solid var(--primary-color);
  border-color: var(--primary-color);
  transition: border 0.5s;
  &:hover {
    border: 2px solid var(--primary-color);
  }
  cursor: pointer;
  min-width: 142px;
  height: 50px;
  background: ${(props) => (props.primary ? "var(--gradient)" : "transparent")};
  box-shadow: ${(props) => (props.primary ? "0px 3px 6px #0000005e" : "none")};
  border-radius: 5px;
  border: 0;
  opacity: 1;
  text-align: center;
  font: normal normal 900 15px Cairo;
  letter-spacing: 0px;
  color: ${(props) => (props.primary ? "white" : "black")};
  opacity: 1;
  @media (max-width: 1440px) {
    font-size: 12px;
  }
`;
const StopButton = styled.button`
  position: relative;
  border: 0px solid var(--primary-color);
  border-color: var(--primary-color);
  transition: border 0.5s;
  &:hover {
    scale: 0.96;
  }
  cursor: pointer;
  width: 180px;
  height: 40px;
  background: red;
  box-shadow: 0px 3px 6px #0000005e;
  border-radius: 5px;
  border: 0;
  opacity: 1;
  text-align: center;
  font: normal normal 900 16px Cairo;
  letter-spacing: 0px;
  color: white;
  opacity: 1;
  @media (max-width: 1440px) {
    font-size: 16px;
  }
`;

export const selectedContext = createContext();
export default function ShipmentsReport() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState([]);
  const navigate = useNavigate();
  const [refetchKey, setRefetchKey] = useState(1);
  const [fetchingData, setFetchingData] = useState(true);

  const [mergedData, setMergedData] = useState([]);
  const [queryState, setQueryState] = useState({
    allShipments: true,
    delivered: false,
    returned: false,
    processing: false,
    heading_to_customer: false,
    new_orders: false,
    require_attention: false,
    pending_return: false,
    paid: false,
    expected: false,
  });

  const { t } = useTranslation("reports");
  const categories = [
    { statusID: 0, name: "allShipments" },
    { statusID: 5, name: "delivered" },
    { statusID: 4, name: "heading_to_customer" },
    { statusID: 3, name: "processing" },
    { statusID: 1, name: "new_orders" },
    { statusID: 6, name: "require_attention" },
    { statusID: 7, name: "pending_return" },
    { statusID: 8, name: "returned" },
    { statusID: -1, name: "paid" },
    { statusID: -2, name: "expected" },
  ];
  const statusID = categories.find(
    (category) =>
      category.name ===
      Object.entries(queryState).find((entry) => entry[1] === true)?.[0]
  )?.statusID;
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const toDate = new Date();
  toDate.setDate(toDate.getDate() + 1);
  toDate.setHours(toDate.getHours() + 3);

  const fetchShipments = async (statusID) => {
    if (!fetchingData) return;
    await delay(300);
    try {
      const instance = axiosInstance();
      const data = await instance.post("/GetShipmentsPage", {
        mainStatusID: statusID,
        FromDate: "2020-04-20T13:23:30.141Z",
        ToDate: toDate.toISOString(),
        pageParam: {
          pageSize: 500,
          pageNumber: refetchKey,
        },
      });

      if (data.data.shipments.length == 0) {
        setFetchingData(false);
      }
      return data.data.shipments.map((shipment) => ({
        ...shipment,
        NetCOD: shipment.COD - shipment.ShipmentFees,
        runnerData: `
          ${shipment.RunnerName}  /
          ${shipment.RunnerMobile}
          `,
      }));
    } catch (error) {
      setFetchingData(false);
      if (error.response && error.response.status === 500) {
        navigate("/server-error");
      }
    }
  };

  const { refetch } = useQuery(
    ["reports", statusID, refetchKey],
    () => fetchShipments(statusID),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      cacheTime: 1000,
      staleTime: 1000,
      onSuccess: (data) => {
        if (data && data.length > 0) {
          setRefetchKey((prev) => prev + 1);
          setMergedData((prevData) => [...prevData, ...data]);
        }
      },
    }
  );

  useEffect(() => {
    if (statusID !== undefined && statusID !== null) {
      refetch();
    }
  }, [statusID, refetch]);

  const buttons = {
    allShipments: false,
    delivered: false,
    heading_to_customer: false,
    processing: false,
    new_orders: false,
    require_attention: false,
    pending_return: false,
    returned: false,
    paid: false,
    expected: false,
  };
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.has("report")) {
      const status = searchParams.get("report");
      const statuses = [
        "processing",
        "returned",
        "paid",
        "expected",
        "allShipments",
        "delivered",
        "heading_to_customer",
        "new_orders",
        "require_attention",
        "pending_return",
      ];
      if (statuses.includes(status)) {
        setQueryState({ ...buttons, [status]: true });
      }
    }
  }, [searchParams]);
  const changeUrl = (value) => {
    setSearchParams(`report=${value}`);
  };

  function changeActiveButton(e) {
    setFetchingData(true);
    queryClient.removeQueries("reports");
    setRefetchKey(1);
    let status = e.target.dataset.status;
    changeUrl(status);
    setQueryState({ ...buttons, [status]: true });
    setMergedData([]);
  }

  const handleStopButtonClick = () => {
    setFetchingData(false);
    setRefetchKey(1);
  };

  console.log(fetchingData);
  return (
    <selectedContext.Provider value={{ selected, setSelected }}>
      <ModernHeader>
        <ModernTitle>{t("shipments_reports")}</ModernTitle>
        <ModernButtonsContainer>
          {fetchingData && (
            <ModernStopButton onClick={handleStopButtonClick}>
              {t("stop_fetching_data")}
            </ModernStopButton>
          )}
          <NewPrintBtn print="shipments" data={selected} />
        </ModernButtonsContainer>
      </ModernHeader>
      
      <ModernTableContainer>
        <ModernButtonsContainer style={{ marginBottom: '20px' }}>
          <ModernButton
            primary={queryState.allShipments}
            data-status="allShipments"
            onClick={changeActiveButton}
          >
            {t("allShipments")}
            {queryState.allShipments && fetchingData && <LoadingButton />}
          </ModernButton>
          <ModernButton
            primary={queryState.delivered}
            data-status="delivered"
            onClick={changeActiveButton}
          >
            {t("delivered")}
            {queryState.delivered && fetchingData && <LoadingButton />}
          </ModernButton>
          <ModernButton
            primary={queryState.heading_to_customer}
            data-status="heading_to_customer"
            onClick={changeActiveButton}
          >
            {t("heading_to_customer")}
            {queryState.heading_to_customer && fetchingData && <LoadingButton />}
          </ModernButton>
          <ModernButton
            primary={queryState.processing}
            data-status="processing"
            onClick={changeActiveButton}
          >
            {t("processing")}
            {queryState.processing && fetchingData && <LoadingButton />}
          </ModernButton>
          <ModernButton
            primary={queryState.new_orders}
            data-status="new_orders"
            onClick={changeActiveButton}
          >
            {t("new_orders")}
            {queryState.new_orders && fetchingData && <LoadingButton />}
          </ModernButton>
          <ModernButton
            primary={queryState.require_attention}
            data-status="require_attention"
            onClick={changeActiveButton}
          >
            {t("require_attention")}
            {queryState.require_attention && fetchingData && <LoadingButton />}
          </ModernButton>
          <ModernButton
            primary={queryState.pending_return}
            data-status="pending_return"
            onClick={changeActiveButton}
          >
            {t("pending_return")}
            {queryState.pending_return && fetchingData && <LoadingButton />}
          </ModernButton>
          <ModernButton
            primary={queryState.returned}
            data-status="returned"
            onClick={changeActiveButton}
          >
            {t("returned")}
            {queryState.returned && fetchingData && <LoadingButton />}
          </ModernButton>
          <ModernButton
            primary={queryState.paid}
            data-status="paid"
            onClick={changeActiveButton}
          >
            {t("received_payments")}
            {queryState.paid && fetchingData && <LoadingButton />}
          </ModernButton>
          <ModernButton
            primary={queryState.expected}
            data-status="expected"
            onClick={changeActiveButton}
          >
            {t("expected_payments")}
            {queryState.expected && fetchingData && <LoadingButton />}
          </ModernButton>
        </ModernButtonsContainer>
        <ShipmentsReportsTable rows={mergedData} />
      </ModernTableContainer>
    </selectedContext.Provider>
  );
}
